import React, { useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import videoManager from '../utils/videoManager';

interface FullScreenVideoPlayerProps {
  videoUrl: string;
  isVisible: boolean;
  onClose: () => void;
  autoPlay?: boolean;
}

const FullScreenVideoOverlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: ${props => props.isVisible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const FullScreenVideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 100vw;
  max-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VideoPlayer = styled.video`
  width: 100%;
  height: 100%;
  max-width: 100vw;
  max-height: 100vh;
  object-fit: contain;
  background: #000;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  background: rgba(0, 0, 0, 0.7);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }

  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
    font-size: 20px;
    top: 10px;
    right: 10px;
  }
`;

const ControlsOverlay = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  padding: 10px 20px;
  border-radius: 25px;
  color: white;
  font-size: 14px;
  z-index: 10000;

  @media (max-width: 480px) {
    bottom: 10px;
    padding: 8px 16px;
    font-size: 12px;
  }
`;

const FullScreenVideoPlayer: React.FC<FullScreenVideoPlayerProps> = ({
  videoUrl,
  isVisible,
  onClose,
  autoPlay = true
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleClose = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
    }

    // Unlock orientation
    if (window.screen?.orientation?.unlock) {
      window.screen.orientation.unlock();
    }

    onClose();
  }, [onClose]);

  // Handle escape key to close fullscreen
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isVisible) {
        handleClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when fullscreen is open
      document.body.style.overflow = 'hidden';

      // 暂停其他视频并设置当前视频
      videoManager.pauseCurrentVideo();
      if (videoRef.current) {
        videoManager.setCurrentVideo(videoRef.current);
      }

      // Enable landscape orientation on mobile if supported
      if (window.screen?.orientation?.lock) {
        window.screen.orientation.lock('landscape').catch(() => {
          // Ignore if orientation lock fails
        });
      }
    } else {
      document.body.style.overflow = 'unset';
      // 清除当前视频引用
      videoManager.clearCurrentVideo();
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isVisible, handleClose]);

  const handleVideoEnd = () => {
    handleClose();
  };

  if (!isVisible) return null;

  return (
    <FullScreenVideoOverlay isVisible={isVisible}>
      <FullScreenVideoContainer>
        <CloseButton
          onClick={handleClose}
          title="关闭全屏"
          aria-label="关闭全屏"
        >
          ×
        </CloseButton>
        <ControlsOverlay>
          按 ESC 键或点击 × 关闭全屏
        </ControlsOverlay>
        <VideoPlayer
          ref={videoRef}
          controls
          autoPlay={autoPlay}
          onEnded={handleVideoEnd}
          playsInline
        >
          <source src={videoUrl} type="video/mp4" />
          <source src={videoUrl} type="video/webm" />
          您的浏览器不支持视频播放。
        </VideoPlayer>
      </FullScreenVideoContainer>
    </FullScreenVideoOverlay>
  );
};

export default FullScreenVideoPlayer;